import React, { type MouseEventHandler } from 'react'
import { Link, NavLink } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { NS } from '~/enums'
import { classNames as cn } from '~/helpers/string'
import { Button } from '~/components/shared'
import { RocketIcon } from '~/components/icons/RocketIcon'
import { HomeIcon } from '~/components/icons/HomeIcon'
import { AccountsIcon } from '~/components/icons/AccountsIcon'
import { MarketDataIcon } from '~/components/icons/MarketDataIcon'
import { PayoutsIcon } from '~/components/icons/PayoutsIcon'
import { BillingIcon } from '~/components/icons/BillingIcon'
import { LeaderboardIcon } from '~/components/icons/LeaderboardIcon'
import { EducationIcon } from '~/components/icons/EducationIcon'
import { VideosIcon } from '~/components/icons/VideosIcon'
import { CalendarIcon } from '~/components/icons/CalendarIcon'
import { ClipboardIcon } from '~/components/icons/ClipboardIcon'
import { WebsiteIcon } from '~/components/icons/WebsiteIcon'
import DiscordIcon from '~/components/icons/Discord'
import { QCStorage } from './quick-checkout/QCsessionStorage'

type SideNavProps = {
  hiddenMenuItems: string[]
  navToggle: MouseEventHandler
  locale: string
  mainDomainUrl: string
}

const socialMediaChannels = [
  {
    id: 'social-facebook-btn',
    src: '/images/socials/facebook-outline.svg',
    alt: 'social-facebook',
    href: 'https://www.facebook.com/TheTradingPitGroup',
  },
  {
    id: 'social-instagram-btn',
    src: '/images/socials/instagram-outline.svg',
    alt: 'social-instagram',
    href: 'https://www.instagram.com/thetradingpitofficial/',
  },
  {
    id: 'social-twitter-btn',
    src: '/images/socials/twitter-outline.svg',
    alt: 'social-twitter',
    href: 'https://twitter.com/TheTradingPit_',
  },
  {
    id: 'social-linkedin-btn',
    src: '/images/socials/linkedin-outline.svg',
    alt: 'social-linkedin',
    href: 'https://www.linkedin.com/company/thetradingpitofficial/',
  },
  {
    id: 'social-youtube-btn',
    src: '/images/socials/youtube-outline.svg',
    alt: 'social-youtube',
    href: 'https://www.youtube.com/@TheTradingPitOfficial/',
  },
  {
    id: 'social-reddit-btn',
    src: '/images/socials/reddit-outline.svg',
    alt: 'social-reddit',
    href: 'https://www.reddit.com/user/The_Trading_Pit',
  },
  {
    id: 'social-tiktok-btn',
    src: '/images/socials/tiktok-outline.svg',
    alt: 'social-tiktok',
    href: 'https://www.tiktok.com/@thetradingpitofficial',
  },
  {
    id: 'social-telegram-btn',
    src: '/images/socials/telegram-outline.svg',
    alt: 'social-telegram',
    href: 'https://t.me/TheTradingPitOfficial_Telegram',
  },
]

export default function SideNav(props: SideNavProps) {
  let { t } = useTranslation()

  const items = [
    {
      name: 'My Client Area',
      icon: HomeIcon,
      title: t('my_client_area', { ns: NS.GLOBALS }),
      to: '/',
    },
    {
      name: 'Accounts',
      icon: AccountsIcon,
      title: t('accounts', { ns: NS.GLOBALS }),
      to: '/accounts',
    },
    {
      name: 'Market Data',
      icon: MarketDataIcon,
      title: t('market_data', { ns: NS.GLOBALS }),
      to: '/market-data',
    },
    {
      name: 'Payouts History',
      icon: PayoutsIcon,
      title: t('payouts_history', { ns: NS.GLOBALS }),
      to: '/payouts-history',
    },
    {
      name: 'Billing History',
      icon: BillingIcon,
      title: t('billing_history', { ns: NS.GLOBALS }),
      to: '/billing-history',
    },
    null,
    {
      name: 'Leaderboard',
      icon: LeaderboardIcon,
      title: t('leaderboard', { ns: NS.GLOBALS }),
      to: '/leaderboard',
    },
    {
      name: 'LiveSquawk',
      icon: EducationIcon,
      title: 'LiveSquawk',
      to: '/livesquawk',
      reloadDocument: true,
    },
    {
      name: 'Educational Videos',
      icon: VideosIcon,
      title: t('educational_videos', { ns: NS.GLOBALS }),
      to: '/educational-videos',
    },
    {
      name: 'Economic Calendar',
      icon: CalendarIcon,
      title: t('economic_calendar', { ns: NS.GLOBALS }),
      to: '/economic-calendar',
    },
    {
      name: 'Trading Rules',
      icon: ClipboardIcon,
      title: t('trading_rules', { ns: NS.GLOBALS }),
      to: '/trading-rules',
    },
    null,
    {
      name: 'Website',
      icon: WebsiteIcon,
      title: t('website', { ns: NS.GLOBALS }),
      to:
        props.locale === 'en'
          ? `${props.mainDomainUrl}/`
          : props.locale === 'zh'
            ? `${props.mainDomainUrl}/zh-hans`
            : `${props.mainDomainUrl}/${props.locale}`,
      target: '_blank',
    },
  ]

  return (
    <div className="group/nav flex h-full flex-col gap-5 overflow-y-auto overflow-x-hidden p-6 pb-16 pt-[5.375rem] lg:max-xl:overflow-y-hidden lg:max-xl:hover:overflow-y-auto">
      {!props.hiddenMenuItems.includes('Start Challenge') && (
        <NavLink
          // IMPORTANT: id for GTM
          id="start-challenge-btn"
          to="/challenges"
          className="flex flex-row items-center justify-center gap-2 rounded-lg bg-success-600 px-6 py-3 transition-all hover:bg-success-650 group-hover/nav:px-6 lg:max-xl:justify-start lg:max-xl:px-3 group-hover/nav:lg:max-xl:justify-center"
          onClick={(event) => {
            QCStorage.clearAllCheckoutData()
            props.navToggle(event)
          }}
        >
          <RocketIcon className="text-brand-base-content h-6 w-6 flex-shrink-0" />
          <span className="text-brand-base-content overflow-hidden truncate whitespace-nowrap text-start text-sm font-semibold">
            {t('start_challenge', { ns: NS.GLOBALS })}
          </span>
        </NavLink>
      )}

      <ul className="flex flex-1 flex-col gap-2.5">
        {React.Children.toArray(
          items.map((item) => {
            if (item === null) {
              return <hr className="my-1.5 bg-[#E5E5E5]" />
            }

            if (!props.hiddenMenuItems.includes(item.name)) {
              return (
                <li>
                  <NavLink
                    to={item.to}
                    onClick={props.navToggle}
                    className={({ isActive }) =>
                      cn(
                        'flex cursor-pointer flex-row items-center justify-start gap-2 rounded-lg px-4 py-2 transition-all group-hover/nav:px-4 lg:max-xl:px-3',
                        isActive ? 'bg-brand-primary-200' : 'hover:bg-brand-primary-50',
                      )
                    }
                    reloadDocument={item.reloadDocument}
                    target={item.target}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          variant={isActive ? 'filled' : 'outlined'}
                          className="text-brand-base-content h-6 w-6 flex-shrink-0"
                        />
                        <span className="text-brand-base-content flex-1 overflow-hidden truncate whitespace-nowrap text-start text-sm font-medium">
                          {item.title}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              )
            }

            return null
          }),
        )}
      </ul>

      <div className="flex flex-col gap-4">
        {!props.hiddenMenuItems.includes('Join Discord') && (
          <Link
            // IMPORTANT: id for GTM
            id="join-discord-btn"
            to="https://discord.com/invite/N4mN72EyZt"
            target="_blank"
            className="w-full"
            onClick={props.navToggle}
          >
            <Button
              variant="outlined"
              className="w-full px-4 transition-all group-hover/nav:gap-2 lg:max-xl:gap-0"
              endIcon={<DiscordIcon className="h-6 w-6 flex-shrink-0" fill="none" />}
            >
              <span className="overflow-hidden truncate whitespace-nowrap lg:max-xl:hidden lg:max-xl:group-hover/nav:inline">
                Discord
              </span>
            </Button>
          </Link>
        )}

        {!props.hiddenMenuItems.includes('Social Media Channels') && (
          <div className="flex items-center justify-between overflow-hidden transition-all delay-150 group-hover/nav:max-h-screen lg:max-xl:max-h-0">
            {React.Children.toArray(
              socialMediaChannels.map((channel) => (
                <img
                  // IMPORTANT: id for GTM
                  id={channel.id}
                  src={channel.src}
                  alt={channel.alt}
                  onClick={() => window.open(channel.href)}
                  className="cursor-pointer transition-transform hover:scale-125"
                />
              )),
            )}
          </div>
        )}
      </div>
    </div>
  )
}
